import { memo, useContext, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { useLanguage } from 'assets/languages';
import { BRAND_DTO_ENUMS } from 'api/repositories/brand/brand.constants';
import { getCampaignCommissionOptions } from 'api/repositories/campaign/campaign.constants';
import { isJapanService } from 'constants/domains/domains';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';
import CampaignFormField from 'pages/campaigns/campaignForm/components/campaignFormContainer/components/campaignFormSteps/components/campaignFormFieldGroup/components/campaignFormField/campaignFormField';
import { CampaignFormFieldWrapperProps } from 'pages/campaigns/campaignForm/components/campaignFormContainer/components/campaignFormSteps/components/campaignFormStepOne/components/formFields/formFields.types';
import SelectCommission from 'components/selectCommission/selectCommission';
import SelectCommissionIncreaseMaxStep from 'components/selectCommissionIncreaseMaxStep/selectCommissionIncreaseMaxStep';
import * as HELPER_CURRENCY from 'helpers/currency';

const CommissionField = ({ elementIndex }: CampaignFormFieldWrapperProps) => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.campaigns };
  const {
    modal,
    brand,
    products,
    settings,
    campaignFormMethods,
    campaignPaymentFormMethods,
    updateCampaignId,
  } = useContext(CampaignFormContext);

  const [commissionAmount, commissionIncreaseMaxStep] = useWatch({
    control: campaignFormMethods.control,
    name: ['commissionAmount', 'commissionIncreaseMaxStep']
  });
  
  const [commissionPrice] = useWatch({
    control: campaignPaymentFormMethods.control,
    name: ['priceCommission']
  });

  const commissionOption = useMemo(() => {
    const commissionOptions = getCampaignCommissionOptions(brand);
    return commissionOptions.find((option) => option.commission === commissionAmount);
  }, [brand, commissionAmount]);

  const amountPerStepByCountry = useMemo(() => {
    switch (brand.currency) {
      case BRAND_DTO_ENUMS.CURRENCY.KRW:
        return 50000
      case BRAND_DTO_ENUMS.CURRENCY.JPY:
        return 5000
      default:
        return 50000
    }
  }, [brand.currency])

  const onClickCommissionField = (commission: number, commissionPrice: number, maxStep: number) => {
    if (updateCampaignId) {
      modal.showAlertModal({
        title: languageTexts.campaign_form_cant_be_update_modal_title,
        text: languageTexts.campaign_form_cant_be_update_modal_text,
      });
    } else {
      campaignFormMethods.setValue('commissionAmount', commission);
      campaignFormMethods.setValue('commissionIncreaseMaxStep', maxStep);
      campaignPaymentFormMethods.setValue('priceCommission', commissionPrice);
    }
  };

  return (
    <CampaignFormField
      label={`${elementIndex}. ${languageTexts.campaign_form_commission_field_label}`}
      description={
        updateCampaignId
          ? `${languageTexts.campaign_form_cant_be_update_modal_title} ${languageTexts.campaign_form_cant_be_update_modal_text}`
          : languageTexts.campaign_form_products_field_desc(
              brand.isCategoryFashion,
            )
      }
    >
      <dl className="flex flex-col mt-4">
        <dt className="text-[15px] font-bold">
          {languageTexts.campaign_form_commission_field_sub_label}
        </dt>
        <dd className="flex flex-col mt-4 gap-[6px]">
        <ul className="gap-[6px] flex flex-row justify-around">
          {getCampaignCommissionOptions(brand).map((option, i) => (
            <li
              key={`commission-option-recommendation-${option.commission}`}
              className="block text-center text-[14px] font-bold"
            >
              {i === 1 ? languageTexts.recommendation : ""}
            </li>
          ))}
        </ul>
          <div className="flex flex-col gap-[10px]">
            <SelectCommission
              brand={brand}
              settings={settings}
              isBig
              selectedValue={commissionPrice}
              onChange={(commission, commissionPrice, maxStep) =>
                onClickCommissionField(commission, commissionPrice, maxStep)
              }
            />
            {isJapanService() && (
              <span className="text-[14px]">
                <a
                  className="underline font-bold"
                  href={isJapanService()
                    ? "https://featpaper.com/l/abUkKRDQ"
                    : "https://glovv.co.kr/redirect/reference"
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  {languageTexts.campaign_form_commission_field_reference_text_1}
                </a>
                {languageTexts.campaign_form_commission_field_reference_text_2}
              </span>
            )}
          </div>
        </dd>
      </dl>
      <div className="flex flex-row justify-between items-center mt-4">
        <div className="flex flex-row items-center">
          <span className="text-[15px] font-bold">
            {languageTexts.campaign_form_commission_field_amount}
          </span>
        </div>
        <div>
          <span className="text-[15px] font-bold">
            {products
              ? products
                  .reduce((sum, product) => sum + (product.qty || 0), 0)
                  .toLocaleString()
              : '0'}
            {languageTexts.campaign_form_commission_field_amount_units}
          </span>
        </div>
      </div>
      <div className="flex flex-row justify-between items-center mt-4">
        <div className="flex flex-row items-center">
          <span className="text-[15px] font-bold">
            {languageTexts.campaign_form_commission_field_total}
          </span>
        </div>
        <div>
          <span className="text-[15px] font-bold">
            {products && commissionPrice
              ? products.reduce((sum, product) => {
                  return sum + (product.qty || 0) * Number(commissionPrice)
                }, 0).toLocaleString()
              : 0}
            {settings.currencyText}
          </span>
        </div>
      </div>
      {!updateCampaignId && (
        <div className="text-sm text-[#999]">
          {languageTexts.campaign_form_commission_field_description}
        </div>
      )}
      {commissionOption?.isStepSelectable && (
        <div className="flex flex-col mt-[20px]">
          <div className="text-[15px] font-bold">
            {languageTexts.campaign_form_commission_field_increase_max_step_label}
          </div>
          <div className="mt-[8px] text-[14px] whitespace-pre-wrap">
            {languageTexts.campaign_form_commission_field_increase_max_step_sub_label(
              HELPER_CURRENCY.makeMoneyText(
                commissionPrice, 10000,
                (moneyText) => languageTexts._price(moneyText, HELPER_CURRENCY.getCurrencySuffixByBrand(settings)),
                (moneyText) => languageTexts._price_10000_short(moneyText, HELPER_CURRENCY.getCurrencySuffixByBrand(settings)),
              )
            )}
          </div>
          <div className="mt-[18px]">
            <SelectCommissionIncreaseMaxStep
              commissionPrice={commissionPrice}
              amountPerStep={amountPerStepByCountry}
              settings={settings}
              maxStep={commissionOption?.maxStep}
              selectedValue={commissionIncreaseMaxStep}
              onChange={(step) => {
                campaignFormMethods.setValue('commissionIncreaseMaxStep', step);
              }}
            />
          </div>
        </div>
      )}
    </CampaignFormField>
  );
};

export default memo(CommissionField);
